<script setup lang="ts">
  /**
   * Links — 03-components/06a-links · OSCloud DS 1.1.47
   */
  import { computed } from 'vue'
  import type { RouteLocationRaw } from 'vue-router'

  type Tone = 'accent' | 'neutral' | 'muted' | 'inverse'

  const props = withDefaults(
    defineProps<{
      to?: RouteLocationRaw | null
      href?: string
      tone?: Tone
      /** Resting underline, for a link inside multi-line copy. */
      inText?: boolean
      type?: 'button' | 'submit'
    }>(),
    {
      to: null,
      href: undefined,
      tone: 'accent',
      inText: false,
      type: 'button',
    },
  )

  defineEmits<{ click: [event: MouseEvent] }>()

  /** A link that acts instead of navigating is still a <button>. */
  const element = computed(() => (props.to ? 'RouterLink' : props.href ? 'a' : 'button'))

  /* Each element gets only its own attributes. An explicit `href: undefined` falls through to the
     <a> RouterLink renders and overwrites the href it resolved, leaving an anchor with no href:
     not a link, not focusable, and inside a <label> a click toggles the control instead. */
  const elementAttrs = computed(() => {
    if (element.value === 'RouterLink') return { to: props.to }
    if (element.value === 'a') return { href: props.href }
    return { type: props.type }
  })
</script>

<template>
  <component
    :is="element"
    class="link"
    :class="[{ 'link--in-text': inText }, tone === 'accent' ? null : `link--${tone}`]"
    v-bind="elementAttrs"
    @click="$emit('click', $event)"
  >
    <span v-if="$slots.icon" class="link__icon"><slot name="icon" /></span>
    <slot />
    <span v-if="$slots.iconTrailing" class="link__icon"><slot name="iconTrailing" /></span>
  </component>
</template>

<style scoped lang="scss">
  .link {
    font: inherit; /* the contract — includes family, size, weight and line-height */
    letter-spacing: inherit;
    color: var(--bf-link);
    text-decoration: none;
    text-underline-offset: var(--bf-link-underline-offset);
    transition: color var(--bf-dur-base) var(--bf-ease);

    &:hover {
      color: var(--bf-link-hover);
      text-decoration: underline;
    }

    /* The same ring the .button recipe draws — a link and a button must not have
       two focus treatments. Written with the palette tokens rather than the
       --dk-* control triplets so it also resolves outside the design kit. */
    &:focus-visible {
      outline: none;
      border-radius: var(--bf-r-sm);
      box-shadow:
        0 0 0 2px var(--bf-white),
        0 0 0 4px var(--bf-orange-dark);
    }

    &--neutral {
      color: var(--bf-link-neutral);

      &:hover {
        color: var(--bf-link-neutral-hover);
      }
    }

    &--muted {
      color: var(--bf-link-muted);

      &:hover {
        color: var(--bf-link-muted-hover);
      }
    }

    &--inverse {
      color: var(--bf-link-inverse);

      &:hover {
        color: var(--bf-link-inverse-hover);
      }

      /* The inverse ring inverts too: the 2px gap is the dark surface, not white. */
      &:focus-visible {
        box-shadow:
          0 0 0 2px var(--bf-deep-blue),
          0 0 0 4px var(--bf-orange-light);
      }
    }

    &--in-text {
      text-decoration: underline;
    }

    &__icon {
      display: inline-flex;
      width: 15px;
      height: 15px;
      flex: none;
      color: currentColor;

      :deep(svg) {
        width: 15px;
        height: 15px;
      }
    }

    &:has(.link__icon) {
      display: inline-flex;
      align-items: center;
      gap: 5px;
    }
  }

  button.link {
    background: none;
    border: 0;
    padding: 0;
    text-align: inherit;
    cursor: pointer;
  }
</style>
